import React, { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import '@repair/catalog/styles.css';

import { useAppDispatch } from '../store/store';
import { InterventionsProvider } from './PlanningPage/InterventionsProvider';
import { CountryProvider } from '../utils/countryProvider/CountryProvider';
import {
  setGTMTrackingID,
  setHasLog,
  setHasMedallia,
  setHideEventNotifications,
  setIsFreeClosingAuthorized,
  setHasFrontMonitoring,
  setIsProd,
  updateDomains,
} from '../store/configSlice';
import { getAppRoute, RoutesPath } from '../core/routes';
import { LazyReact, lazyWithSuspense } from './LazyReact/LazyReact';
import { LoaderProvider } from '../utils/loader/LoaderProvider';
import { type ServiceApiName } from '../utils/config/configuration.type';
import AppErrorBoundary from './AppErrorBoundary';
import { StartUp } from './StartUp/StartUp';
import { PageLayout } from './PageLayout/PageLayout';
import { Authentication } from './auth/Authentication';
import { ProductProvider } from './ProductPage/ProductProvider';
import { ModalProvider } from './shared/Modals/ModalProvider';
import { SearchNavigationProvider } from './ToolbarTop/Header/SearchNavigationContext';
import { CatalogWrapper } from '../wrapper/CatalogWrapper';
import { SideEffects } from './SideEffects';

const SearchPage = lazyWithSuspense(() => import('./SearchPage/SearchPage'));
const HomePage = lazyWithSuspense(() => import('./HomePage/HomePage'));
const SparePartOrdersPage = lazyWithSuspense(async () => ({
  default: (await import('../pages/SparePartOrders/SparePartOrders')).SparePartOrders,
}));
const InterventionPage = lazyWithSuspense(async () => ({
  default: (await import('./InterventionPage/InterventionPage')).InterventionPage,
}));
const PlanningPage = lazyWithSuspense(() => import('./PlanningPage/PlanningPage'));
const ShippingPage = lazyWithSuspense(async () => ({
  default: (await import('../features/shipping/components/ShippingPage/ShippingPage')).ShippingPage,
}));

const ErrorPage = lazyWithSuspense(async () => ({
  default: (await import('./ErrorPage/ErrorPage')).ErrorPage,
}));
const MassiveInvoicePage = lazyWithSuspense(() => import('./MassiveInvoicePage/MassiveInvoicePage'));
const InterventionsContainer = lazyWithSuspense(() => import('./InterventionsPlanningResult/InterventionsPlanningResultContainer'));
const DelaysParameters = lazyWithSuspense(() => import('./DelaysParameters/DelaysParameters'));
const LoadForecastPage = lazyWithSuspense(() => import('./LoadForecastPage/LoadForecastPage'));
const ProductPage = lazyWithSuspense(async () => ({
  default: (await import('./ProductPage/ProductPage')).ProductPage,
}));
const CustomerPage = lazyWithSuspense(() => import('./Customer/CustomerPage'));
const CustomerAddOrUpdate = lazyWithSuspense(async () => ({
  default: (await import('./CustomerAddOrUpdate/CustomerAddOrUpdate')).CustomerAddOrUpdate,
}));
const ShippingGroup = lazyWithSuspense(() => import('./ShippingGroup/ShippingGroup'));
const TableSearch = lazyWithSuspense(() => import('./TableSearch/TableSearch'));
const InterventionsListPage = lazyWithSuspense(async () => ({
  default: (await import('../pages/Interventions/InterventionsListPageWrapper')).InterventionsListPageWrapper,
}));
const AdminPage = lazyWithSuspense(() => import('./AdminPage/AdminPage'));
const BookingsPage = lazyWithSuspense(async () => ({
  default: (await import('../pages/Bookings')).BookingsPage,
}));
const ProcessesCatalogPage = lazyWithSuspense(async () => ({
  default: (await import('../pages/Catalog/ProcessesCatalogPage')).ProcessesCatalogPage,
}));
const CategoriesCatalogPage = lazyWithSuspense(async () => ({
  default: (await import('../pages/Catalog/CategoriesCatalogPage')).CategoriesCatalogPage,
}));
const ProductsCatalogPage = lazyWithSuspense(async () => ({
  default: (await import('../pages/Catalog/ProductsCatalogPage')).ProductsCatalogPage,
}));
const NewProcessesCatalogPage = lazyWithSuspense(async () => ({
  default: (await import('../pages/Catalog/NewProcessesCatalogPage')).NewProcessesCatalogPage,
}));
const GroupNatureLevelTwoPage = lazyWithSuspense(async () => ({
  default: (await import('../pages/Catalog/GroupNatureLevelTwoPage')).GroupNatureLevelTwoPage,
}));
const NewProductsCatalogPage = lazyWithSuspense(async () => ({
  default: (await import('../pages/Catalog/NewProductsCatalogPage')).NewProductsCatalogPage,
}));

const ProductSheetPage = lazyWithSuspense(async () => ({
  default: (await import('../pages/ProductSheet/ProductSheet')).ProductSheet,
}));
const AccessibilityPage = lazyWithSuspense(async () => import('../pages/Accessibility/AccessibilityPage'));
const UsefulLinksPage = lazyWithSuspense(async () => import('../pages/UsefulLinks/UsefulLinksPage'));

interface ConfigSecretUrl {
  isProd: boolean;
  hasFrontMonitoring: boolean;
  hasMedallia: boolean;
  GTMTrackingID: string;
  isFreeClosingAuthorized: boolean;
  hideEventNotifications: boolean;
  urls: Record<ServiceApiName, string>;
  hasLog?: boolean;
}

const headers = new Headers();
headers.append('pragma', 'no-cache');
headers.append('cache-control', 'no-cache');

export const App = () => {
  const dispatch = useAppDispatch();

  const [userReady, setUserReady] = useState(false);
  const [appError, setAppError] = useState(false);

  useEffect(() => {
    fetch(new URL('/config.env.json', location.origin).toString(), { headers })
      .then(async response => {
        const configResponse: ConfigSecretUrl = await response.json();

        dispatch(setIsProd(configResponse.isProd));
        dispatch(setHasMedallia(configResponse.hasMedallia));
        dispatch(setGTMTrackingID(configResponse.GTMTrackingID));
        dispatch(setIsFreeClosingAuthorized(configResponse.isFreeClosingAuthorized));
        dispatch(setHideEventNotifications(configResponse.hideEventNotifications));
        dispatch(setHasLog(configResponse.isProd || Boolean(configResponse.hasLog)));

        dispatch(updateDomains({ urls: configResponse.urls }));
        dispatch(setHasFrontMonitoring(configResponse.hasFrontMonitoring));

        if (!userReady) {
          setUserReady(true);
        }
      })
      .catch(() => {
        setAppError(true);
      });
  }, []);

  if (!userReady) {
    return null;
  }

  if (appError) {
    return (
      <>
        <p>An unexpected error occurred. Please try again later.</p>;
        <SideEffects />
      </>
    );
  }

  if (location.pathname === getAppRoute(RoutesPath.adminPage)) {
    return (
      <Routes>
        <Route path={getAppRoute(RoutesPath.adminPage)} element={<AdminPage />} />
      </Routes>
    );
  }

  return (
    <LazyReact>
      <SideEffects />
      <AppErrorBoundary>
        <Authentication>
          <LoaderProvider>
            <SearchNavigationProvider>
              <StartUp>
                <CatalogWrapper>
                  <ModalProvider>
                    <CountryProvider>
                      <ProductProvider>
                        <PageLayout>
                          <InterventionsProvider>
                            <Routes>
                              <Route path={getAppRoute(RoutesPath.homePage)} element={<HomePage />} />
                              <Route path={getAppRoute(RoutesPath.sparePartOrders)} element={<SparePartOrdersPage />} />
                              <Route path={getAppRoute(RoutesPath.interventionPage, '*')}>
                                <Route path=":interventionId" element={<InterventionPage />} />
                                <Route path="" element={<InterventionPage />} />
                              </Route>
                              <Route path={getAppRoute(RoutesPath.shippingPage)} element={<ShippingPage />} />
                              <Route path={getAppRoute(RoutesPath.massiveInvoicePage)} element={<MassiveInvoicePage />} />
                              <Route path={getAppRoute(RoutesPath.customerPage, ':memberId')} element={<CustomerPage />} />
                              <Route path={getAppRoute(RoutesPath.customerCreatePage)} element={<CustomerAddOrUpdate />} />
                              <Route path={getAppRoute(RoutesPath.customerUpdatePage, ':memberId')} element={<CustomerAddOrUpdate />} />
                              <Route path={getAppRoute(RoutesPath.errorPage)} element={<ErrorPage />} />
                              <Route path={getAppRoute(RoutesPath.productPage, '*')}>
                                <Route path=":type" element={<ProductPage />} />
                                <Route path="" element={<ProductPage />} />
                              </Route>
                              <Route path={getAppRoute(RoutesPath.shippingGroupPage, ':externalRef')} element={<ShippingGroup />} />
                              <Route path={getAppRoute(RoutesPath.planningPage)} element={<PlanningPage />} />
                              <Route path={getAppRoute(RoutesPath.searchPage)} element={<SearchPage />} />
                              <Route path={getAppRoute(RoutesPath.searchResultPage)} element={<TableSearch />} />
                              <Route path={getAppRoute(RoutesPath.interventionsListPage)} element={<InterventionsListPage pageType="search" />} />
                              <Route
                                path={getAppRoute(RoutesPath.interventionsPartnersListPage)}
                                element={<InterventionsListPage pageType="partners" />}
                              />
                              <Route
                                path={getAppRoute(RoutesPath.interventionsCustomerPage)}
                                element={<InterventionsListPage pageType="customer" />}
                              />
                              <Route
                                path={getAppRoute(RoutesPath.interventionsCompleted)}
                                element={<InterventionsListPage pageType="searchCompleted" />}
                              />
                              <Route path={getAppRoute(RoutesPath.planningInterventionPage)} element={<InterventionsContainer />} />
                              <Route path={getAppRoute(RoutesPath.forecastPage)} element={<LoadForecastPage />} />
                              <Route path={getAppRoute(RoutesPath.delayParamPage)} element={<DelaysParameters />} />
                              <Route path={getAppRoute(RoutesPath.bookingsPage)} element={<BookingsPage />} />
                              <Route path={getAppRoute(RoutesPath.catalogPage, '*')}>
                                <Route path="processes" element={<ProcessesCatalogPage />} />
                                <Route path="processes/:processId/categories" element={<CategoriesCatalogPage />} />
                                <Route path="processes/:processId/categories/:categoryId/products/*" element={<ProductsCatalogPage />} />
                              </Route>
                              <Route path={getAppRoute(RoutesPath.newCatalogSportsPage, '*')}>
                                <Route path="processes" element={<NewProcessesCatalogPage />} />
                                <Route path="processes/:processId/product-nature-groups" element={<GroupNatureLevelTwoPage />} />
                                <Route
                                  element={<NewProductsCatalogPage />}
                                  path="processes/:processId/product-nature-groups/:groupNatureLevelTwoId/products/*"
                                />
                              </Route>
                              <Route path={getAppRoute(RoutesPath.productSheetPage, ':productId')} element={<ProductSheetPage />} />
                              <Route path={getAppRoute(RoutesPath.productSheetPage, 'custom')} element={<ProductSheetPage />} />
                              <Route path={getAppRoute(RoutesPath.usefulLinksPage)} element={<UsefulLinksPage />} />
                              <Route path={getAppRoute(RoutesPath.accessibilityPage)} element={<AccessibilityPage />} />
                              <Route path="*" element={<Navigate to={getAppRoute(RoutesPath.homePage)} />} />
                            </Routes>
                          </InterventionsProvider>
                        </PageLayout>
                      </ProductProvider>
                    </CountryProvider>
                  </ModalProvider>
                </CatalogWrapper>
              </StartUp>
            </SearchNavigationProvider>
          </LoaderProvider>
        </Authentication>
      </AppErrorBoundary>
    </LazyReact>
  );
};
